import type { StateCreator } from 'zustand';
import type { SidebarPanelContent } from './SidebarPanel';

export interface TaskPanels {
  showSidebarPanel: boolean;
  sidebarPanelContent: SidebarPanelContent | null;
  lastBriefContent: { title: string; content: string } | null;
  showDocumentOverlay: boolean;
  documentOverlayTitle: string | null;
  documentOverlayContent: string | null;
}
export const emptyTaskPanels = (): TaskPanels => ({
  showSidebarPanel: false, sidebarPanelContent: null, lastBriefContent: null,
  showDocumentOverlay: false, documentOverlayTitle: null, documentOverlayContent: null,
});
interface PanelStore extends TaskPanels {
  activeProjectId: number | null;
  taskPanelsByProject?: Record<number, TaskPanels>;
}
const snapshot = (s: TaskPanels): TaskPanels => Object.fromEntries(
  Object.keys(emptyTaskPanels()).map(k => [k, s[k as keyof TaskPanels]]),
) as unknown as TaskPanels;

/** Late async work writes only its captured task, never whichever task is now visible. */
export function taskPanelPatch<T extends PanelStore>(s: T, owner: number | null, patch: Partial<TaskPanels>): Partial<T> {
  if (s.activeProjectId === owner) return patch as Partial<T>;
  if (owner === null) return {};
  return { taskPanelsByProject: { ...s.taskPanelsByProject, [owner]: {
    ...emptyTaskPanels(), ...s.taskPanelsByProject?.[owner], ...patch,
  } } } as Partial<T>;
}

/** Automatic completion cannot replace a brief or report the user is reviewing. */
export function angleSetCompletionPanelPatch<T extends PanelStore>(s: T, owner: number | null, setId: number, title: string): Partial<T> {
  const current = s.activeProjectId === owner ? s : owner === null ? undefined : s.taskPanelsByProject?.[owner];
  if (current?.showSidebarPanel || current?.showDocumentOverlay) return {};
  return taskPanelPatch(s, owner, {
    showSidebarPanel: true,
    sidebarPanelContent: { type: 'angle_set', title, angleSetId: setId, ownerProjectId: owner },
  });
}

/** Apply task navigation and panel restoration atomically, including external setState callers. */
export function withTaskPanels<T extends PanelStore>(creator: StateCreator<T>): StateCreator<T> {
  return (set, get, api) => {
    const scopedSet: typeof set = (update, replace) => {
      const previous = get();
      const next = typeof update === 'function' ? update(previous) : update;
      if (previous && Object.hasOwn(next, 'activeProjectId') && next.activeProjectId !== previous.activeProjectId) {
        const saved = { ...previous.taskPanelsByProject };
        if (previous.activeProjectId !== null) saved[previous.activeProjectId] = snapshot(previous);
        const destination = next.activeProjectId == null ? emptyTaskPanels() : saved[next.activeProjectId] ?? emptyTaskPanels();
        set({ ...next, ...destination, taskPanelsByProject: saved } as T, replace as false);
      } else {
        set(next as T, replace as false);
      }
    };
    api.setState = scopedSet;
    return creator(scopedSet, get, api);
  };
}
